import { Box, Grid, Tooltip, Typography } from "@mui/material";
import BaseButton from "../../../main/components/BaseButton";
import { Client } from "../types";
import { useContext, useEffect, useState } from "react";
import TextInput from "../../../main/components/TextInput";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import ConfirmationDialogUser from "../../users/components/ConfirmationDialogUser";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { clientsApi } from "../../../services/api";
import { PathClientDetails } from "../../../services/api/routes/clients";
import { cnpjRegex, phoneRegex } from "../../../main/utils/utils";
import CnpjInput from "../../../main/components/CnpjInput";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";
import { MainContext } from "../../../main/context/MainContextProvider";

const schema = yup.object().shape({
  cnpj: yup
    .string()
    .nullable()
    .transform((originalValue) => {
      const cleanedValue = originalValue
        ? originalValue.replace(/[^\d]+/g, "")
        : null;
      return cleanedValue;
    })
    .matches(cnpjRegex, "Formato de CNPJ inválido"),
  street: yup.string().nullable(),
  streetNumber: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup.string().nullable(),
  email: yup.string().nullable(),
  phone: yup
    .string()
    .matches(phoneRegex, "Formato de Telefone inválido")
    .nullable(),
});

export type EditClientFormType = yup.InferType<typeof schema>;

export default function ClientDetailsInfos(props: {
  client: Client | null;
  onMount: () => void;
}) {
  const { userIsAdm, userIsClient } = useContext(MainContext);
  const { client, onMount } = props;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [isCancelConfirmationOpen, setCancelConfirmationOpen] =
    useState<boolean>(false);
  const [isSaveConfirmationOpen, setSaveConfirmationOpen] =
    useState<boolean>(false);

  const onSubmit = async () => {
    setSaveConfirmationOpen(true);
  };

  const onOpenEditMode = () => {
    setIsEditing(!isEditing);
  };

  const handleConfirmCancel = () => {
    clearErrors();
    reset({
      cnpj: client?.cnpj || "Não informado",
      street: client?.street || "Não informado",
      streetNumber: client?.streetNumber || "Não informado",
      city: client?.city || "Não informado",
      state: client?.state || "Não informado",
      email: client?.email || "Não informado",
      phone: client?.phone || "Não informado",
    });
    setIsEditing(false);
    setCancelConfirmationOpen(false);
  };

  const handleConfirmSave = async () => {
    const patchData: PathClientDetails = {
      name: client?.name || null,
      cnpj: formData.cnpj || null,
      phone: formData.phone || null,
      zipCode: client?.zipCode || null,
      email: formData.email || null,
      street: formData.street || null,
      streetNumber: formData.streetNumber || null,
      complement: client?.complement || null,
      city: formData.city || null,
      state: formData.state || null,
    };

    try {
      await clientsApi.pathClientDetails(client?.id ?? "", patchData);
      clearErrors();
      setOpenSuccessModal(true);
      setIsEditing(false);
      setSaveConfirmationOpen(false);
    } catch (error) {
      setOpenErrorModal(true);
    }
  };

  const {
    control,
    formState: { errors, isDirty },
    // setValue,
    reset,
    handleSubmit,
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const formData = watch();

  useEffect(() => {
    const clientFormData = {
      cnpj: client?.cnpj || "Não informado",
      street: client?.street || "Não informado",
      streetNumber: client?.streetNumber || "Não informado",
      city: client?.city || "Não informado",
      state: client?.state || "Não informado",
      email: client?.email || "Não informado",
      phone: client?.phone || "Não informado",
    };

    reset(clientFormData);
  }, [client, reset, isEditing]);

  const canConfirmEdit = isDirty;

  return (
    <Grid
      container
      sx={{
        padding: "20px",
        flexDirection: "row",
        display: "flex",
        backgroundColor: "rgba(217, 217, 217, 0.2)",
        borderRadius: "4px",
        marginTop: "8px",
        marginBottom: "8px",
        rowGap: "24px ",
        justifyContent: "space-between",
      }}
      component={"form"}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={6} textAlign={"start"}>
        <Typography>Detalhes</Typography>
      </Grid>
      <Grid item xs={6} textAlign={"end"}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {isEditing ? (
            <>
              <Tooltip
                title={canConfirmEdit ? "" : "Nenhuma alteração identificada"}
              >
                <Box>
                  <PrimaryBaseButton
                    disabled={!canConfirmEdit}
                    title="Salvar"
                    type="submit"
                    sx={{ height: "30px", marginRight: "8px" }}
                  />
                </Box>
              </Tooltip>
              <DangerBaseButton
                title={canConfirmEdit ? "Descartar" : "Cancelar"}
                onClick={() => {
                  canConfirmEdit
                    ? setCancelConfirmationOpen(true)
                    : setIsEditing(false);
                }}
                sx={{
                  height: "30px",
                }}
              />
            </>
          ) : (
            <>
              {(userIsAdm || userIsClient) && (
                <BaseButton
                  title="Editar"
                  onClick={() => onOpenEditMode()}
                  sx={{ height: "30px", width: "90px" }}
                />
              )}
            </>
          )}
        </Box>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          rowGap: "16px",
        }}
      >
        <Grid item xs={12} md={4}>
          <CnpjInput
            disabled={!isEditing}
            label="CNPJ"
            control={control}
            errors={errors}
            fieldName={"cnpj"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput
            disabled={!isEditing}
            label="Cidade"
            control={control}
            errors={errors}
            fieldName={"city"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput
            disabled={!isEditing}
            label="Rua"
            control={control}
            errors={errors}
            fieldName={"street"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput
            disabled={!isEditing}
            label="Número"
            control={control}
            errors={errors}
            fieldName={"streetNumber"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput
            disabled={!isEditing}
            label="Estado"
            control={control}
            errors={errors}
            fieldName={"state"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput
            disabled={!isEditing}
            label="Email"
            control={control}
            errors={errors}
            fieldName={"email"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PhoneNumberInput
            disabled={!isEditing}
            label="Telefone"
            control={control}
            errors={errors}
            fieldName={"phone"}
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
      </Grid>
      <ConfirmationDialogUser
        open={isCancelConfirmationOpen}
        title="Descartar alteração"
        message="Deseja descartar e cancelar as alterações?"
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelConfirmationOpen(false)}
        onClose={() => setCancelConfirmationOpen(false)}
      />
      <ConfirmationModal
        open={isSaveConfirmationOpen}
        setOpen={() => setSaveConfirmationOpen(false)}
        title="Editar detalhes"
        contentText="Deseja confirmar a edição dos detalhes ?"
        onConfirm={handleConfirmSave}
      />
      <ActionAlert
        message="Os detalhes foram atualizados com sucesso!"
        severity="success"
        open={openSuccessModal}
        setOpen={() => setOpenSuccessModal(false)}
        onClose={onMount}
      />
      <ActionAlert
        message="Ocorreu um erro no modo de edição! Tente novamente mais tarde."
        severity="error"
        open={openErrorModal}
        setOpen={setOpenErrorModal}
      // onClose={() => {
      //   setShowAddItemModal(false);
      // }}
      />
      {/* <SuccessModal
        open={openSuccessModal}
        setOpen={() => setOpenSuccessModal(false)}
        title="Detalhes do Cliente atualizado com Sucesso!"
        contentText="Os detalhes foram atualizados com sucesso!"
        onClose={onMount}
      />
      <ErrorModal
        open={openErrorModal}
        setOpen={setOpenErrorModal}
        title="Erro ao editar os dados!"
        contentText="Ocorreu um erro no modo de edição! Tente novamente mais tarde."
      /> */}
    </Grid>
  );
}
